import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MultiPhotoUpload } from "@/components/MultiPhotoUpload";
import { STAGE_META } from "@/lib/plant";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const STAGES = Object.keys(STAGE_META);

export function LogEntryDialog({ open, onOpenChange, onSaved, plant }) {
  const [form, setForm] = useState({ date: "", growth_stage: "", height_cm: "", note: "", photo_paths: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        date: new Date().toISOString().slice(0, 10),
        growth_stage: plant?.growth_stage || "Seedling",
        height_cm: "",
        note: "",
        photo_paths: [],
      });
    }
  }, [open, plant]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/plants/${plant.id}/logs`, {
        date: form.date,
        growth_stage: form.growth_stage,
        height_cm: form.height_cm === "" ? null : Number(form.height_cm),
        note: form.note,
        photo_paths: form.photo_paths,
      });
      toast.success("Growth logged 🌿");
      onOpenChange(false);
      onSaved?.();
    } catch {
      toast.error("Could not save entry");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="log-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Log Growth Entry</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <MultiPhotoUpload value={form.photo_paths} onChange={(p) => set("photo_paths", p)} testId="log-photo" />
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Date</Label>
              <Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="log-date-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Height (cm)</Label>
              <Input
                type="number"
                min={0}
                step="0.1"
                value={form.height_cm}
                onChange={(e) => set("height_cm", e.target.value)}
                placeholder="e.g. 12.5"
                data-testid="log-height-input"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Growth stage</Label>
            <Select value={form.growth_stage} onValueChange={(v) => set("growth_stage", v)}>
              <SelectTrigger data-testid="log-stage-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STAGES.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`log-stage-option-${s}`}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Note</Label>
            <Textarea value={form.note} onChange={(e) => set("note", e.target.value)} placeholder="First true leaves appeared…" rows={3} data-testid="log-note-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="log-dialog-cancel">
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="log-entry-submit-button">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save entry
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
